import { supabase } from "@/integrations/supabase/client";
import { EventHostInvites, EventNpoInvites } from "@/integrations/supabase/types/events";

export type InviteType = 'npo' | 'host';

export type EventInvite = {
  id: string;
  slug: string;
  npo_id: string;
  status: string;
  host_id: string | null;
  location: string;
  created_at: string;
  event_date: string;
  event_name: string;
  event_time: string;
  updated_at: string;
  description: string;
  corporate_id: string;
  corporate_profiles: {
    company_name: string;
  };
  preferred_language: string | null;
  corporate_contact_id: string | null;
  expected_participants: number | null;
  opportunity_catalog_id: string;
  event_id: string;
  company_name: string;
};

export const getNPOEventInvite = async (eventId: string, npoId: string) => {
  const { data, error } = await supabase
    .from("event_npo_invites")
    .select("*")
    .eq("event_id", eventId)
    .eq("npo_id", npoId)
    .single();

  if (error) throw error;
  return data as EventNpoInvites["Row"];
};

export const getHostEventInvite = async (eventId: string, hostId: string) => {
  const { data, error } = await supabase
    .from("event_host_invites")
    .select("*")
    .eq("event_id", eventId)
    .eq("host_id", hostId)
    .single();

  if (error) throw error;
  return data as EventHostInvites["Row"];
};

export const getPendingEventInvites = async (type: InviteType, userId: string) => {
  const table = type === 'npo' ? 'event_npo_invites' : 'event_host_invites';
  const userColumn = type === 'npo' ? 'npo_id' : 'host_id';
  
  const { data, error } = await supabase
    .from(table)
    .select(`
      *,
      created_at,
      event:events!inner (
        event_name,
        event_date,
        event_time,
        location,
        opportunity_catalog:opportunity_catalog_id (
          id,
          format,
          format_details
        ),
        corporate_profiles (
          company_name
        )
      )
    `)
    .eq(userColumn, userId)
    .eq("status", "pending");

  if (error) throw error;
  return data as EventInvite[];
};

export const getAllEventInvites = async (
  type: 'npo' | 'host',
  token: string | undefined
) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/me/invites?type=${type}`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const { data } = await response.json();
    return data as EventInvite[];
  } catch (error) {
    console.error('Error fetching invites:', error);
    return [];
  }
};

export const updateEventInviteStatus = async (
  id: string, 
  status: "accepted" | "declined", 
  type: InviteType
) => {
  const table = type === 'npo' ? 'event_npo_invites' : 'event_host_invites';
  
  const { error } = await supabase
    .from(table)
    .update({ status })
    .eq("id", id);

  if (error) throw error;
  return { success: true };
};